import type { GetServerSideProps } from 'next';
import React from 'react';
import { useRouter } from 'next/router';
import { Trans } from 'next-i18next';
import { Error } from '@layout/Error';
import { HTTP_STATUS_CODES } from '@constants/http-status-codes';
import { useTranslationResponse } from '@isomorphic/hooks/use-translation-response';
import { useCurrentPlatform } from '@client/hooks/use-current-platform';
import { Link, Typography } from '@mui/material';
import { PlatformsList } from '@layout/components/Platforms/PlatformsList';
import { Styled403ListBox, StyledError } from '@layout/error/Error.styles';
import { useUserContext } from '@contexts/User';
import { getSignInLink } from '@utils/header';
import { getErrorPageServerSideProps } from '@utils/errorPageProps';
import Head from 'next/head';

const Error403 = (props) => {
  const router = useRouter();
  const { resolvedUrl } = router.query;
  const title = useTranslationResponse('403MainTitle');
  const text = useTranslationResponse('403MainTitleTextAuth');
  const linkText = useTranslationResponse('403SignIn');
  const request = useTranslationResponse('403RequestAccess');
  const follow = useTranslationResponse('403FollowSteps');
  const account = useUserContext();
  const platform = useCurrentPlatform();
  const loginLink: string = getSignInLink(props.publicRuntimeSettings, resolvedUrl as string);

  return (
    <>
      <Head>
        <title>{`${useTranslationResponse('403MainTitle')}`}</title>
      </Head>
      <Error statusCode={HTTP_STATUS_CODES['403']} showNav {...props} navTree={props.navTree}>
        <StyledError className="error-page">
          <section className="error-page__block error-page__block--error-403">
            <div className="error-page__content error-page__content--error-403">
              <Typography variant="h1" className="error-page__title">
                {title}
              </Typography>
              <div className="error-page__text">
                {account?.isLoggedIn ? (
                  text
                ) : (
                  <Trans i18nKey="403HaveToLogin">
                    {loginLink}
                    <Link color="primary" target="_blank" href={loginLink}>
                      {linkText}
                    </Link>
                  </Trans>
                )}
              </div>
              <Styled403ListBox>
                <Typography variant="h4" className="page-403__h4">
                  {request} to {platform}?
                </Typography>
                <Typography variant="overline">{follow}</Typography>
                <PlatformsList platform={platform} />
              </Styled403ListBox>
            </div>
          </section>
        </StyledError>
      </Error>
    </>
  );
};

export default Error403;

export const getServerSideProps: GetServerSideProps = async ({ req, query }) =>
  getErrorPageServerSideProps(req, query.resolvedUrl as string);
